import React, { useState } from "react";
import { X, UserPlus, ListTodo, Wallet, Loader2, Plus } from "lucide-react";
import { api, adminHeaders } from "@/lib/api";
import { toast } from "sonner";

const h = () => ({ headers: adminHeaders() });

const input =
  "w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-3.5 py-2.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]";
const label = "block text-xs uppercase tracking-wide text-muted-c mb-1.5";

const EMPTY = {
  lead: { full_name: "", phone: "", email: "", service_required: "", city: "", source: "phone", notes: "" },
  task: { title: "", priority: "normal", due_date: "", notes: "" },
  expense: { category: "", amount: "", vendor: "", date: "", notes: "" },
};

export const AdminQuickAdd = ({ open, onClose, isOwner, onCreated }) => {
  const tabs = [
    { id: "lead", label: "Lead", icon: UserPlus },
    { id: "task", label: "Task", icon: ListTodo },
    ...(isOwner ? [{ id: "expense", label: "Expense", icon: Wallet }] : []),
  ];
  const [tab, setTab] = useState("lead");
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const upd = (t, k) => (e) => setForm((f) => ({ ...f, [t]: { ...f[t], [k]: e.target.value } }));
  const reset = () => setForm({ ...EMPTY, lead: { ...EMPTY.lead }, task: { ...EMPTY.task }, expense: { ...EMPTY.expense } });

  const submit = async () => {
    setBusy(true);
    try {
      if (tab === "lead") {
        if (!form.lead.full_name.trim()) { toast.error("Name is required"); setBusy(false); return; }
        await api.post("/admin/leads", form.lead, h());
        toast.success("Lead added");
      } else if (tab === "task") {
        if (!form.task.title.trim()) { toast.error("Title is required"); setBusy(false); return; }
        await api.post("/admin/tasks", form.task, h());
        toast.success("Task added");
      } else if (tab === "expense") {
        if (!form.expense.category.trim() || !form.expense.amount) { toast.error("Category and amount are required"); setBusy(false); return; }
        await api.post("/finance/expenses", { ...form.expense, amount: Number(form.expense.amount) }, h());
        toast.success("Expense logged");
      }
      reset();
      onCreated && onCreated(tab);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center p-4 pt-20" role="dialog" aria-modal="true" data-testid="quick-add-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !busy && onClose()} />
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-[color:var(--border-strong)] bg-[#0F0F10] shadow-[var(--shadow-3)]">
        <div className="card-accent-top h-1 w-full" />
        <div className="flex items-center justify-between px-6 pt-5">
          <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2"><Plus className="h-4 w-4 text-gold-soft" /> Quick add</h3>
          <button onClick={onClose} data-testid="quick-add-close" className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-white/60 hover:text-white hover:bg-white/5"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex gap-2 px-6 mt-4">
          {tabs.map(({ id, label: l, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`quick-add-tab-${id}`}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm border transition-colors ${tab === id ? "bg-gold text-black border-gold" : "border-[color:var(--border-subtle)] text-white/80 hover:bg-white/5"}`}>
              <Icon className="h-4 w-4" /> {l}
            </button>
          ))}
        </div>

        <div className="px-6 py-5 space-y-3.5 max-h-[60vh] overflow-y-auto">
          {tab === "lead" && (
            <>
              <div><label className={label}>Full name *</label><input data-testid="qa-lead-name" className={input} value={form.lead.full_name} onChange={upd("lead", "full_name")} placeholder="e.g. Dana Cohen" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Phone</label><input data-testid="qa-lead-phone" className={input} value={form.lead.phone} onChange={upd("lead", "phone")} placeholder="05x-xxx-xxxx" /></div>
                <div><label className={label}>Email</label><input className={input} value={form.lead.email} onChange={upd("lead", "email")} placeholder="optional" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Service</label><input className={input} value={form.lead.service_required} onChange={upd("lead", "service_required")} placeholder="e.g. Deep Clean" /></div>
                <div><label className={label}>City</label><input className={input} value={form.lead.city} onChange={upd("lead", "city")} placeholder="e.g. Tel Aviv" /></div>
              </div>
              <div>
                <label className={label}>Source</label>
                <select className={input} value={form.lead.source} onChange={upd("lead", "source")} data-testid="qa-lead-source">
                  <option value="phone">Phone</option><option value="whatsapp">WhatsApp</option>
                  <option value="walk-in">Walk-in</option><option value="referral">Referral</option><option value="manual">Other</option>
                </select>
              </div>
              <div><label className={label}>Notes</label><textarea rows={2} className={input} value={form.lead.notes} onChange={upd("lead", "notes")} placeholder="What did they ask for?" /></div>
            </>
          )}
          {tab === "task" && (
            <>
              <div><label className={label}>Task *</label><input data-testid="qa-task-title" className={input} value={form.task.title} onChange={upd("task", "title")} placeholder="e.g. Call Dana back with quote" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Priority</label>
                  <select className={input} value={form.task.priority} onChange={upd("task", "priority")} data-testid="qa-task-priority">
                    <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
                  </select>
                </div>
                <div><label className={label}>Due date</label><input type="date" className={input} value={form.task.due_date} onChange={upd("task", "due_date")} data-testid="qa-task-due" /></div>
              </div>
              <div><label className={label}>Notes</label><textarea rows={2} className={input} value={form.task.notes} onChange={upd("task", "notes")} /></div>
            </>
          )}
          {tab === "expense" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Category *</label><input data-testid="qa-exp-category" className={input} value={form.expense.category} onChange={upd("expense", "category")} placeholder="e.g. Supplies" /></div>
                <div><label className={label}>Amount (₪) *</label><input data-testid="qa-exp-amount" type="number" className={input} value={form.expense.amount} onChange={upd("expense", "amount")} placeholder="0" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Vendor</label><input className={input} value={form.expense.vendor} onChange={upd("expense", "vendor")} placeholder="optional" /></div>
                <div><label className={label}>Date</label><input type="date" className={input} value={form.expense.date} onChange={upd("expense", "date")} /></div>
              </div>
              <div><label className={label}>Notes</label><textarea rows={2} className={input} value={form.expense.notes} onChange={upd("expense", "notes")} /></div>
            </>
          )}
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose} disabled={busy} className="flex-1 rounded-xl border border-[color:var(--border-subtle)] py-2.5 text-sm text-white/70 hover:bg-white/5 disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="quick-add-submit" className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gold py-2.5 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminQuickAdd;
